import React, { useEffect, useRef } from "react";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import { useState } from "react";
import { Box, IconButton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getAllGoogleMapGuides } from "../../services/guides";
import {
  setCityGuides,
  setShowMessageObj,
  setUserCoordinates,
} from "../../store/userSlice";
import blueDotImg from "../../assets/blueDotImg.svg";
import redDotImg from "../../assets/redDotImg.svg";
import { handleRandomId } from "../../helpers/Utils";
import closeImg from "../../assets/closeImg.svg";
import closeMenuImage from "../../assets/closeMenuImage.svg";
import coordinatesImg from "../../assets/coordinatesImg.svg";
import { APPERROR, TRUEVALUE } from "../../helpers/Config";
import { trackMixpanel } from "../../services/mixpanel";

const GoogleMapComponent = ({
  center = { lat: 48.3794, lng: 31.1656 },
  setCenter,
  searchValue,
  setSearchValue,
  zoomLevel,
  setZoomLevel,
  handleSearchValue,
  handleGetGuides,
}) => {
  const cityGuides = useSelector((state) => state.userSlice.cityGuides);
  const user = useSelector((state) => state.userSlice.user);
  const userCoordinates = useSelector(
    (state) => state.userSlice.userCoordinates
  );
  const [selectedCity, setSelectedCity] = useState(null);
  const [newMarker, setNewMarker] = useState(null);
  const [isFullView, setIsFullView] = useState(false);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  let locationStorage = localStorage.getItem('location')
  const dispatch = useDispatch();
  const mapRef = useRef(null);
  // console.log("user", user);

  const containerStyle = {
    width: "100%",
    // aspectRatio: 1 / 1.3,
    // height: "auto",
    // height: "100%",
    // maxHeight: "430px",
    height: isFullView ? "380px" : "165px",
    minHeight: isFullView ? '100%' : "165px",
    maxHeight: isFullView ? "380px" : "165px",
    borderRadius: "24px",
  };

  const handleSelectedCity = (city) => {
    setSelectedCity(city);
    // handleSearchValue(city.name);
    // setNewMarker(null);
    setCenter({ lat: Number(city.lat), lng: Number(city.lng) });
    setSearchValue(city.name);
    handleGetGuides(city.name, 0);
    setZoomLevel(8);
    if (mapRef.current) {
      mapRef.current.panTo({ lat: Number(city.lat), lng: Number(city.lng) });
    }
  };

  const handleMapClick = (e) => {
    setSelectedCity(null);
    const newMarker = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };
    getCityFromCoordinates(newMarker.lat, newMarker.lng).then((city) => {
      handleSearchValue(city);
      setNewMarker({ ...newMarker, id: handleRandomId(), name: city });
    });
  };

  const isLocationEnabled = async () => {
    if (!navigator.permissions) return true;

    try {
      const permission = await navigator.permissions.query({
        name: "geolocation",
      });
      console.log("Geolocation permission status:", permission.state);
      return permission.state !== "denied";
    } catch (error) {
      console.log("Error checking location permission:", error);
      return false;
    }
  };

  const getLocation = async () => {
    try {
      // const locationEnabled = await isLocationEnabled();

      // if (!locationEnabled) {
      //   alert(
      //     "Location services are disabled. Please enable them to use the app."
      //   );
      //   // return null;
      // }

      let coordinates;

      try {
        coordinates = await getCoordinates();
      } catch (error) {
        console.log("navigator.geolocation failed:", error.message);
      }

      if (!coordinates) {
        console.log("Trying getGoogleGeolocation...");
        coordinates = await getGoogleGeolocation();
      }

      if (coordinates) {
        const city = await getCityFromCoordinates(
          coordinates.latitude,
          coordinates.longitude
        );
        setCenter({ lat: coordinates.latitude, lng: coordinates.longitude });
        dispatch(
          setUserCoordinates({
            lat: coordinates.latitude,
            lng: coordinates.longitude,
          })
        );
        return city;
      } else {
        console.log("Could not determine city");
        return null;
      }
    } catch (error) {
      console.log("Error while determining the city:", error.message);
      return null;
    }
  };

  const getCoordinates = () => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            console.log("position.coords", position.coords);
            setCenter({ lat: latitude, lng: longitude });
            resolve({ latitude, longitude });
          },
          (err) => {
            console.log("Error obtaining geolocation: " + err.message);
            reject(err);
          },
          { enableHighAccuracy: true, timeout: 7000, maximumAge: 0 }
        );
      } else {
        console.log("Geolocation is not supported by your browser.");
        reject(new Error("Geolocation not supported"));
      }
    });
  };

  const getGoogleGeolocation = async () => {
    try {
      const response = await fetch(
        "https://www.googleapis.com/geolocation/v1/geolocate?key=" +
          process.env.REACT_APP_GOOGLE_MAP_ID,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (data.location) {
        return {
          latitude: data.location.lat,
          longitude: data.location.lng,
        };
      } else {
        console.error("Geolocation not found");
        return null;
      }
    } catch (error) {
      console.error("Error fetching geolocation:", error);
      return null;
    }
  };

  const getCityFromCoordinates = async (latitude, longitude) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_MAP_ID}`
      );
      const data = await response.json();
  
      if (data.status === "OK" && data.results.length > 0) {
        const addressComponents = data.results[0].address_components;
  
        const city = addressComponents.find((component) =>
          component.types.includes("locality")
        )?.long_name;
  
        const region = addressComponents.find((component) =>
          component.types.includes("administrative_area_level_1")
        )?.long_name;
  
        const country = addressComponents.find((component) =>
          component.types.includes("country")
        )?.long_name;
  
        if (!city && !region && !country) {
          dispatch(
            setShowMessageObj({
              open: true,
              status: "error",
              message: "In this region, we found nothing.",
            })
          );
          trackMixpanel(APPERROR, {
            errorMessage: "In this region, we found nothing.",
            timestamp: new Date().toISOString(),
          });
        }

        if (!city && !region && country) {
          setZoomLevel(2);
        } else {
          setZoomLevel(8);
        }
  
        return city || region || country || "Unknown";
      } else {
        console.error("City not found");
        return null;
      }
    } catch (error) {
      console.error("Error fetching location:", error);
      return null;
    }
  };  

  useEffect(() => {
    if (user?.id) {
      if (!searchValue?.length) {
        setSelectedCity(null);
        setNewMarker(null);
        setZoomLevel(2);
      }

      // if (locationStorage && locationStorage !== TRUEVALUE) {
      //   setSelectedCity(null);
      //   setNewMarker(null);
      //   setZoomLevel(2);
      //   setSearchValue("");
      //   handleGetGuides("", 0);
      // } 
    }
  // }, [searchValue, user, locationStorage]);
  }, [searchValue, user]);

  useEffect(() => {
    if (user?.id) {
      const fetchLocation = async () => {
        const res = await getLocation();
        if (res) {
          setSearchValue(res);
          handleGetGuides(res, 0);
        } else {
          console.log("Could not determine city");
          handleGetGuides(searchValue, 0);
        }
      };

      // if (!locationStorage || (locationStorage && locationStorage === TRUEVALUE)) {
      if (locationStorage && locationStorage === TRUEVALUE) {
        fetchLocation();
      } else {
        handleGetGuides(searchValue, 0);
      }
    }
  }, [user]);

  useEffect(() => {
    if (isMapLoaded && cityGuides?.length) {
      window.google.maps.event.trigger(mapRef.current, "resize");
    }
  }, [isMapLoaded, cityGuides]);

  useEffect(() => {
    const fetchCityGuides = async () => {
      const googleMapGuides = await getAllGoogleMapGuides();
      dispatch(
        setCityGuides(
          googleMapGuides?.cities?.length ? googleMapGuides.cities : []
        )
      );
    };

    fetchCityGuides();

  }, []);


  return (
    <Box
      // mt={2}
      sx={{
        // height: '180px',
        position: "relative",
        width: "100%",
        // height: 'auto',
        // aspectRatio: 1 / 1.3,
        // maxHeight: "430px",
        zIndex: 99,
        height: isFullView ? "380px" : "165px",
        // minHeight: "165px",
        minHeight: isFullView ? "380px" : "165px",
        maxHeight: isFullView ? "380px" : "165px",
        "& .gmnoprint": { display: "none !important" },
        "& .gm-control-active": { display: "none !important" },
        "& .gm-style-cc": { display: "none !important" },
        '& a[href^="https://maps.google.com/maps"]': {
          display: "none !important",
        },
      }}
    >
      <IconButton
        size="large"
        sx={{
          position: "absolute",
          bottom: "4px",
          right: "4px",
          zIndex: 100,
          padding: "0",
          backgroundColor: "white",
          "&:hover": { backgroundColor: "white" },
        }}
        onClick={() => setIsFullView(!isFullView)}
      >
        <Box
          component="img"
          src={isFullView ? closeMenuImage : closeImg}
          alt="img"
          sx={{
            width: "40px",
            height: "40px",
          }}
        />
      </IconButton>
      {!isMapLoaded && !selectedCity?.id && (
        <Box
          component="img"
          src={`https://maps.googleapis.com/maps/api/staticmap?center=${center.lat},${center.lng}&zoom=6&size=600x300&maptype=roadmap&key=${process.env.REACT_APP_GOOGLE_MAP_ID}`}
          alt="Loading..."
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 10,
            borderRadius: "24px",
          }}
        />
      )}
      <LoadScript googleMapsApiKey={`${process.env.REACT_APP_GOOGLE_MAP_ID}`} loadingElement={<></>} >
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          // center={
          //   newMarker
          //     ? { lat: Number(newMarker.lat), lng: Number(newMarker.lng) }
          //     : center
          // }
          // zoom={6}
          zoom={zoomLevel}
          // onLoad={() => setIsMapLoaded(true)}
          // onLoad={(map) => {
          //   mapRef.current = map;
          //   setIsMapLoaded(true);
          // }}
          onLoad={(map) => {
            mapRef.current = map;
            setIsMapLoaded(true);
            setTimeout(() => {
              window.google.maps.event.trigger(map, "resize");
              map.panTo(center); 
            }, 500);
          }}
          // onClick={handleMapClick}
        >
          {/* {((userCoordinates && userCoordinates?.lat) && (!locationStorage || (locationStorage && locationStorage === TRUEVALUE))) && ( */}
          {((userCoordinates && userCoordinates?.lat) && (locationStorage && locationStorage === TRUEVALUE)) && (
            <Marker
              key={user.id}
              position={{
                lat: Number(userCoordinates.lat),
                lng: Number(userCoordinates.lng),
              }}
              icon={{
                url: coordinatesImg,
              }}
            />
          )}
          {newMarker && newMarker.name === searchValue && (
            <Marker
              key={newMarker.id}
              position={{
                lat: Number(newMarker.lat),
                lng: Number(newMarker.lng),
              }}
              icon={{
                url: blueDotImg,
              }}
            />
          )}
          {!!cityGuides?.length &&
            cityGuides.map((city) => (
              <Marker
                key={city.id}
                position={{ lat: Number(city.lat), lng: Number(city.lng) }}
                onClick={() => handleSelectedCity(city)}
                icon={{
                  url:
                    selectedCity?.id === city?.id ||
                    (searchValue?.length && searchValue === city?.name)
                      ? blueDotImg
                      : redDotImg,
                }}
              />
            ))}

          {/* {selectedCity && (
                  <InfoWindow
                    position={selectedCity.position}
                    onCloseClick={() => setSelectedCity(null)}
                  >
                    <Box>
                      <Typography variant="h6" fontWeight={600}>{selectedCity.name}</Typography>
                      <Typography variant="body1">Number of guides: {selectedCity.guidesCount}</Typography>
                    </Box>
                  </InfoWindow>
                )} */}
        </GoogleMap>
      </LoadScript>
    </Box>
  );
};

export default GoogleMapComponent;
